import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMeeting, API } from "../App";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Cpu, ArrowLeft, ArrowRight, Info, CheckCircle2, ImageIcon, X, Settings } from "lucide-react";
import { usePresentationMode } from "../components/Layout";
import ImageLibraryManager from "../components/ImageLibraryManager";
import AssetConfigManager from "../components/AssetConfigManager";
import InteractiveColumns from "../components/InteractiveColumns";
import { MOTOR_DATA, getSimpleMotorComponents } from "../data/motorData";
import axios from "axios";

// Default data - will be overridden by API config if available
const DEFAULT_COMPONENTS = MOTOR_DATA;

const MotorPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token } = useAuth();
  const { currentMeeting, toggleAsset, isAssetSelected } = useMeeting();
  const { isPresentationMode, enterPresentationMode } = usePresentationMode();
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [isImageLibraryOpen, setIsImageLibraryOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [customImage, setCustomImage] = useState(null);
  const [components, setComponents] = useState(DEFAULT_COMPONENTS);

  // Auto-enter presentation mode if query param is present
  useEffect(() => {
    if (searchParams.get('presentation') === 'true' && !isPresentationMode) {
      enterPresentationMode();
      // Remove the query param after entering presentation mode
      searchParams.delete('presentation');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, isPresentationMode, enterPresentationMode, setSearchParams]);

  useEffect(() => {
    fetchActiveImage();
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      // Priority 1: Check asset-configs (user customizations)
      try {
        const configResp = await axios.get(`${API}/asset-configs/motors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (configResp.data && configResp.data.components && configResp.data.components.length > 0) {
          setComponents(configResp.data.components);
          return;
        }
      } catch (e) { /* no custom config */ }
      // Priority 2: Check asset-types (admin-seeded dynamic config)
      try {
        const response = await axios.get(`${API}/asset-types/motors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data && response.data.components && response.data.components.length > 0) {
          setComponents(response.data.components);
          return;
        }
      } catch (e) { /* no dynamic config */ }
    } catch (error) {
      console.error("Using default configuration");
    }
  };

  const handleConfigSaved = (config) => {
    if (config && config.components && config.components.length > 0) {
      setComponents(config.components);
    }
  };

  const fetchActiveImage = async () => {
    try {
      const response = await axios.get(`${API}/asset-images/motors/active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setCustomImage(response.data);
      }
    } catch (error) {
      console.error("No active image found");
    }
  };

  if (!currentMeeting) {
    navigate("/landing");
    return null;
  }

  const handleComponentClick = (component) => {
    const fullComponent = components.find(c => c.id === component.id || c.id === String(component.number));
    setSelectedComponent(selectedComponent?.id === (component.id || String(component.number)) ? null : fullComponent);
  };

  const handleMarkerClick = (componentId) => {
    const fullComponent = components.find(c => c.id === componentId);
    setSelectedComponent(selectedComponent?.id === componentId ? null : fullComponent);
  };

  const handleSelectItem = (type, itemName, componentId) => {
    toggleAsset(type, `${componentId}-${itemName}`, "motors");
  };

  const isItemSelected = (type, itemName, componentId) => {
    return isAssetSelected(type, `${componentId}-${itemName}`, "motors");
  };

  const handleImageSaved = (imageData) => {
    setCustomImage(imageData);
  };

  const closeDetails = () => {
    setSelectedComponent(null);
  };

  // Default SVG Schematic for Motor
  const MotorSchematic = () => (
    <svg viewBox="0 0 400 300" className="w-full h-full" style={{ maxHeight: "380px" }}>
      <defs>
        <linearGradient id="motorBodyGrad" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#64748B" />
          <stop offset="50%" stopColor="#475569" />
          <stop offset="100%" stopColor="#334155" />
        </linearGradient>
      </defs>
      
      <rect x="0" y="0" width="400" height="300" fill="#F1F5F9" />
      
      {/* Motor Body */}
      <ellipse cx="200" cy="150" rx="120" ry="80" fill="url(#motorBodyGrad)" stroke="#1E293B" strokeWidth="3" />
      
      {/* Cooling Fins */}
      {Array.from({ length: 8 }, (_, i) => (
        <rect key={i} x="90" y={85 + i * 16} width="220" height="10" fill="#334155" stroke="#1E293B" strokeWidth="1" rx="1" />
      ))}
      
      {/* End Bells */}
      <ellipse cx="80" cy="150" rx="30" ry="60" fill="#475569" stroke="#1E293B" strokeWidth="2" />
      <ellipse cx="320" cy="150" rx="30" ry="60" fill="#475569" stroke="#1E293B" strokeWidth="2" />
      
      {/* Shaft */}
      <rect x="340" y="140" width="50" height="20" fill="#94A3B8" stroke="#64748B" strokeWidth="2" rx="2" />
      <ellipse cx="390" cy="150" rx="8" ry="10" fill="#CBD5E1" stroke="#94A3B8" strokeWidth="1" />
      
      {/* Junction Box */}
      <rect x="160" y="50" width="80" height="40" fill="#1E293B" stroke="#0F172A" strokeWidth="2" rx="3" />
      <circle cx="180" cy="70" r="5" fill="#22C55E" />
      <circle cx="200" cy="70" r="5" fill="#FBBF24" />
      <circle cx="220" cy="70" r="5" fill="#EF4444" />
      
      {/* Fan Cover */}
      <ellipse cx="50" cy="150" rx="20" ry="50" fill="#64748B" stroke="#475569" strokeWidth="2" />
      <line x1="50" y1="100" x2="50" y2="200" stroke="#334155" strokeWidth="2" />
      <line x1="30" y1="150" x2="70" y2="150" stroke="#334155" strokeWidth="2" />
      
      {/* Mounting Feet */}
      <rect x="100" y="220" width="60" height="15" fill="#334155" stroke="#1E293B" strokeWidth="2" rx="2" />
      <rect x="240" y="220" width="60" height="15" fill="#334155" stroke="#1E293B" strokeWidth="2" rx="2" />
      
      {/* Component Markers */}
      {components.map((comp, idx) => {
        const positions = [
          { x: 80, y: 150 },
          { x: 200, y: 150 },
          { x: 200, y: 100 },
          { x: 50, y: 100 },
          { x: 365, y: 150 }
        ];
        return (
          <g key={comp.id} className="cursor-pointer" onClick={() => handleComponentClick(comp)}>
            <circle
              cx={positions[idx].x}
              cy={positions[idx].y}
              r="16"
              fill={comp.color}
              stroke="white"
              strokeWidth="3"
              style={{ filter: selectedComponent?.id === comp.id ? `drop-shadow(0 0 8px ${comp.color})` : "drop-shadow(0 2px 4px rgba(0,0,0,0.3))" }}
            />
            <text x={positions[idx].x} y={positions[idx].y + 5} textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">
              {comp.number}
            </text>
          </g>
        );
      })}
    </svg>
  );

  // Presentation Mode with Detail View
  if (isPresentationMode && selectedComponent) {
    return (
      <div data-testid="motor-page" className="h-full">
        <div className="grid grid-cols-12 gap-6 h-full">
          {/* Left - Compact Diagram */}
          <div className="col-span-4">
            <div className="bg-gradient-to-b from-green-50 to-slate-100 rounded-lg border border-slate-200 p-3 h-full flex flex-col">
              <div className="flex-1 relative flex items-center justify-center">
                {customImage ? (
                  <div className="relative inline-block">
                    <img 
                      src={customImage.image_url} 
                      alt={customImage.image_name}
                      className="max-w-full rounded"
                      style={{ maxHeight: "320px" }}
                    />
                    {customImage.markers?.map((marker) => {
                      const component = components.find(c => c.id === marker.component_id);
                      if (!component) return null;
                      return (
                        <button
                          key={marker.component_id}
                          onClick={() => handleMarkerClick(marker.component_id)}
                          className={`absolute w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm transition-all ${
                            selectedComponent?.id === marker.component_id 
                              ? 'scale-125 ring-2 ring-white shadow-xl' 
                              : 'hover:scale-110 shadow-md'
                          }`}
                          style={{
                            left: `${marker.x}%`,
                            top: `${marker.y}%`,
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: component.color,
                            border: "2px solid white"
                          }}
                        >
                          {component.number}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <MotorSchematic />
                )}
              </div>
              
              {/* Component Quick Nav */}
              <div className="flex justify-center gap-2 mt-3 pt-3 border-t border-slate-200">
                {components.map((comp) => (
                  <button
                    key={comp.id}
                    onClick={() => handleComponentClick(comp)}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold transition-all ${
                      selectedComponent?.id === comp.id ? 'scale-110 ring-2 ring-offset-2' : 'opacity-70 hover:opacity-100'
                    }`}
                    style={{ backgroundColor: comp.color, '--tw-ring-color': comp.color }}
                    title={comp.name}
                  >
                    {comp.number}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Component Details */}
          <div className="col-span-8">
            {/* Component Header */}
            <div 
              className="flex items-center justify-between mb-4 pb-4 border-b-2"
              style={{ borderColor: selectedComponent.color }}
            >
              <div className="flex items-center gap-4">
                <div 
                  className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-xl shadow-lg"
                  style={{ backgroundColor: selectedComponent.color }}
                >
                  {selectedComponent.number}
                </div>
                <h2 className="text-2xl font-semibold text-heading text-slate-900">
                  {selectedComponent.name}
                </h2>
              </div>
              <button
                onClick={closeDetails}
                className="p-2 hover:bg-slate-100 rounded-full transition-colors"
                title="Back to overview"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Three Columns - Using InteractiveColumns */}
            <InteractiveColumns
              component={selectedComponent}
              onSelectItem={handleSelectItem}
              isItemSelected={isItemSelected}
              compact={true}
            />
          </div>
        </div>

        <ImageLibraryManager
          isOpen={isImageLibraryOpen}
          onClose={() => setIsImageLibraryOpen(false)}
          assetType="motors"
          componentLabels={components.map(c => c.name)}
          onImageSaved={handleImageSaved}
        />
      </div>
    );
  }

  // Presentation Mode - Overview
  if (isPresentationMode) {
    return (
      <div data-testid="motor-page">
        <div className="grid grid-cols-12 gap-4">
          {/* Left Panel */}
          <div className="col-span-3 space-y-2">
            {components.filter((_, i) => i < 3).map((component) => {
              const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
              return (
                <Card 
                  key={component.id}
                  className="cursor-pointer transition-all duration-200 hover:shadow-md"
                  onClick={() => handleComponentClick(component)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div 
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: component.color }}
                      >
                        {component.number}
                      </div>
                      <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                    </div>
                    <ul className="text-xs text-slate-500 space-y-0.5 ml-9">
                      {failureNames.slice(0, 2).map((mode, idx) => (
                        <li key={idx}>• {mode}</li>
                      ))}
                      <li className="text-blue-600">+ {failureNames.length - 2} more...</li>
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Center - Schematic */}
          <div className="col-span-6">
            <div className="relative bg-gradient-to-b from-green-50 to-slate-100 rounded-lg border border-slate-200 p-3 flex items-center justify-center" style={{ minHeight: "420px" }}>
              {customImage ? (
                <div className="relative inline-block">
                  <img 
                    src={customImage.image_url} 
                    alt={customImage.image_name}
                    className="max-w-full rounded"
                    style={{ maxHeight: "380px" }}
                  />
                  {customImage.markers?.map((marker) => {
                    const component = components.find(c => c.id === marker.component_id);
                    if (!component) return null;
                    return (
                      <button
                        key={marker.component_id}
                        onClick={() => handleMarkerClick(marker.component_id)}
                        className="absolute w-9 h-9 rounded-full flex items-center justify-center text-white font-bold transition-all hover:scale-110 shadow-lg"
                        style={{
                          left: `${marker.x}%`,
                          top: `${marker.y}%`,
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: component.color,
                          border: "2px solid white"
                        }}
                      >
                        {component.number}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <MotorSchematic />
              )}
              
              <div className="absolute bottom-2 left-2 right-2 bg-white/80 backdrop-blur-sm rounded px-3 py-2 text-center">
                <p className="text-xs text-slate-500">Click on any component to view details</p>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="col-span-3 space-y-2">
            {components.filter((_, i) => i >= 3).map((component) => {
              const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
              return (
                <Card 
                  key={component.id}
                  className="cursor-pointer transition-all duration-200 hover:shadow-md"
                  onClick={() => handleComponentClick(component)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <div 
                        className="w-7 h-7 rounded-full flex items-center justify-center text-white font-bold text-sm"
                        style={{ backgroundColor: component.color }}
                      >
                        {component.number}
                      </div>
                      <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                    </div>
                    <ul className="text-xs text-slate-500 space-y-0.5 ml-9">
                      {failureNames.slice(0, 2).map((mode, idx) => (
                        <li key={idx}>• {mode}</li>
                      ))}
                      <li className="text-blue-600">+ {failureNames.length - 2} more...</li>
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <ImageLibraryManager
          isOpen={isImageLibraryOpen}
          onClose={() => setIsImageLibraryOpen(false)}
          assetType="motors"
          componentLabels={components.map(c => c.name)}
          onImageSaved={handleImageSaved}
        />
      </div>
    );
  }

  // Normal Mode
  return (
    <div data-testid="motor-page" className="min-h-screen">
      {!isPresentationMode && (
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-50 rounded-md flex items-center justify-center">
              <Cpu className="w-6 h-6 text-green-600" strokeWidth={1.5} />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-heading text-slate-900">Motor Condition Monitoring</h1>
              <p className="text-slate-500">Click on numbered components to explore failure modes and solutions</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => setIsConfigOpen(true)} className="gap-2">
              <Settings className="w-4 h-4" strokeWidth={1.5} />
              Configure
            </Button>
            <Button variant="outline" onClick={() => setIsImageLibraryOpen(true)} className="gap-2">
              <ImageIcon className="w-4 h-4" strokeWidth={1.5} />
              Images
            </Button>
            <Button variant="outline" onClick={() => navigate("/assets/switchgear")}>
              <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Switchgear
            </Button>
            <Button onClick={() => navigate("/summary")} className="bg-blue-900 hover:bg-blue-800">
              Generate Summary
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-3 space-y-3">
          {components.filter((_, i) => i < 3).map((component) => {
            const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
            return (
              <Card 
                key={component.id}
                className={`cursor-pointer transition-all duration-200 ${selectedComponent?.id === component.id ? 'ring-2 shadow-lg' : 'hover:shadow-md'}`}
                style={{ borderColor: selectedComponent?.id === component.id ? component.color : undefined, '--tw-ring-color': component.color }}
                onClick={() => handleComponentClick(component)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: component.color }}>
                      {component.number}
                    </div>
                    <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                  </div>
                  <ul className="text-xs text-slate-500 space-y-1 ml-11">
                    {failureNames.slice(0, 3).map((mode, idx) => (<li key={idx}>• {mode}</li>))}
                    {failureNames.length > 3 && <li className="text-blue-600">+ {failureNames.length - 3} more...</li>}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="col-span-6">
          <div className="relative bg-gradient-to-b from-green-50 to-slate-100 rounded-lg border border-slate-200 p-4 flex items-center justify-center" style={{ minHeight: "520px" }}>
            {customImage ? (
              <div className="relative inline-block">
                <img src={customImage.image_url} alt={customImage.image_name} className="max-w-full rounded" style={{ maxHeight: "480px" }} />
                {customImage.markers?.map((marker) => {
                  const component = components.find(c => c.id === marker.component_id);
                  if (!component) return null;
                  return (
                    <button
                      key={marker.component_id}
                      onClick={() => handleMarkerClick(marker.component_id)}
                      className={`absolute w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-lg transition-all duration-200 ${selectedComponent?.id === marker.component_id ? 'scale-125 ring-4 ring-white shadow-xl' : 'hover:scale-110 shadow-lg'}`}
                      style={{ left: `${marker.x}%`, top: `${marker.y}%`, transform: 'translate(-50%, -50%)', backgroundColor: component.color, border: "3px solid white" }}
                    >
                      {component.number}
                    </button>
                  );
                })}
              </div>
            ) : (
              <MotorSchematic />
            )}
            <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-sm rounded-md p-3 border border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                <Info className="w-3 h-3 inline mr-1" />
                {customImage ? `Using: ${customImage.image_name}` : "Click on numbered markers or component cards to view details"}
              </p>
            </div>
          </div>
        </div>

        <div className="col-span-3 space-y-3">
          {components.filter((_, i) => i >= 3).map((component) => {
            const failureNames = component.failureModes.map(f => typeof f === 'string' ? f : f.name);
            return (
              <Card 
                key={component.id}
                className={`cursor-pointer transition-all duration-200 ${selectedComponent?.id === component.id ? 'ring-2 shadow-lg' : 'hover:shadow-md'}`}
                style={{ borderColor: selectedComponent?.id === component.id ? component.color : undefined, '--tw-ring-color': component.color }}
                onClick={() => handleComponentClick(component)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: component.color }}>
                      {component.number}
                    </div>
                    <h3 className="font-semibold text-slate-900 text-heading text-sm">{component.name}</h3>
                  </div>
                  <ul className="text-xs text-slate-500 space-y-1 ml-11">
                    {failureNames.slice(0, 3).map((mode, idx) => (<li key={idx}>• {mode}</li>))}
                    {failureNames.length > 3 && <li className="text-blue-600">+ {failureNames.length - 3} more...</li>}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {selectedComponent && (
        <div className="mt-6 animate-slide-up">
          <Card className="border-2" style={{ borderColor: selectedComponent.color }}>
            <CardHeader className="pb-4" style={{ backgroundColor: `${selectedComponent.color}10` }}>
              <CardTitle className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: selectedComponent.color }}>
                  {selectedComponent.number}
                </div>
                <span className="text-heading text-xl">{selectedComponent.name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <InteractiveColumns
                component={selectedComponent}
                onSelectItem={handleSelectItem}
                isItemSelected={isItemSelected}
                compact={false}
              />
            </CardContent>
          </Card>
        </div>
      )}

      <ImageLibraryManager
        isOpen={isImageLibraryOpen}
        onClose={() => setIsImageLibraryOpen(false)}
        assetType="motors"
        componentLabels={components.map(c => c.name)}
        onImageSaved={handleImageSaved}
      />

      <AssetConfigManager
        isOpen={isConfigOpen}
        onClose={() => setIsConfigOpen(false)}
        assetType="motors"
        onConfigSaved={handleConfigSaved}
      />
    </div>
  );
};

export default MotorPage;
